/* eslint-disable no-new */
import { SerializedMeterByView, ViewMeters } from '@enmon/commons/lib/enmon';
import { theme } from 'consts/theme';
import { VFC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MarkerTooltip as messages } from '../../messages';

interface MetersMapProps {
  meters: SerializedMeterByView[ViewMeters.Map][];
}

export const MapContent: VFC<MetersMapProps> = ({ meters }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>();
  const intl = useIntl();
  const metersWithCoordinates = useMemo(() => meters.filter(meter => meter.location?.coordinates), [meters]);

  // NOTE: calculate average coordinates so map is centered on all of them
  const averageLng = useMemo(
    () =>
      metersWithCoordinates.reduce((acc, { location }) => acc + (location?.coordinates?.[0] ?? 0), 0) /
      metersWithCoordinates.length,
    [metersWithCoordinates],
  );
  const averageLat = useMemo(
    () =>
      metersWithCoordinates.reduce((acc, { location }) => acc + (location?.coordinates?.[1] ?? 0), 0) /
      metersWithCoordinates.length,
    [metersWithCoordinates],
  );

  // Since google's marker is outside of Intl context, we need to make this function to create tooltip HTML content
  const createTooltipContent = useCallback(
    (meter: SerializedMeterByView[ViewMeters.Map] | undefined) => {
      const { toolsId, serial: serialNumber } = meter ?? {};
      const serialText = intl.formatMessage(messages.serialNumber, { serialNumber });

      return `
      <div style="display: flex; flex-direction: column; gap: 8px; color: white;">
        <span>
          Id: <a href="/meter/${toolsId}" style="color: ${theme.colors.main}; cursor: pointer;">${toolsId}</a>
        </span>
        <span>${serialText}</span>
      </div>
    `;
    },
    [intl],
  );

  // Initialize map and InfoWindow
  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center: { lat: averageLat, lng: averageLng },
        // NOTE: hide points of interest so that the map is not too distracting
        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        fullscreenControl: false,
        streetViewControl: false,
        zoom: 14,
      });
      setMap(newMap);
      setInfoWindow(new google.maps.InfoWindow());
    }
  }, [map, ref, averageLat, averageLng]);

  // Initialize meter markers
  useEffect(() => {
    if (map && infoWindow && metersWithCoordinates) {
      for (const meter of metersWithCoordinates) {
        const [lng, lat] = meter.location?.coordinates || [];

        if (lat === undefined || lng === undefined) {
          // Skip meters without valid coordinates
          return;
        }

        const marker = new google.maps.Marker({
          position: { lat, lng },
          map,
        });

        // NOTE: click on map closes InfoWindow
        map.addListener('click', () => {
          infoWindow.close();
        });

        // NOTE: click on marker opens InfoWindow(and keeps it open)
        marker.addListener('click', () => {
          infoWindow.setContent(createTooltipContent(meter));
          infoWindow.open({
            anchor: marker,
            map,
          });
        });
      }
    }
    return () => {
      if (map) {
        google.maps.event.clearListeners(map, 'click');
      }
    };
  }, [metersWithCoordinates, map, infoWindow, createTooltipContent]);

  return <div ref={ref} style={{ height: '70vh' }} />;
};
